// Centralized ecommerce event layer (P2.10).
//
// ONE entry point — every event fans out to:
//   - PostHog (already installed, product/funnel analytics)
//   - GA4 gtag (loaded only when REACT_APP_GA4_ID is configured)
//
// Events never double-fire: call sites emit once through track(); purchase
// is additionally guarded per session id. GA4 stays completely inert until a
// Measurement ID is set — no placeholder requests, no fake tracking.

const GA4_ID = process.env.REACT_APP_GA4_ID || "";
let ga4Requested = false;

export const ga4Configured = () => Boolean(GA4_ID);

export const initAnalytics = () => {
  if (!GA4_ID || ga4Requested || typeof window === "undefined") return;
  ga4Requested = true;  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA4_ID)}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  // SPA: disable automatic page_view; route changes send it explicitly.
  window.gtag("config", GA4_ID, { send_page_view: false });
};

// Initialize at MODULE LOAD, not in a React effect: child component effects
// (AnalyticsTracker page_view, view_item, view_cart, ...) run BEFORE App's
// mount effect, so effect-based init silently drops every fresh-landing
// event in production builds. Import-time init guarantees gtag exists
// before any component effect can fire. Idempotent via ga4Requested.
initAnalytics();

export const trackPageview = (path) => {
  try {
    window.gtag?.("event", "page_view", { page_path: path });
  } catch {
    /* analytics must never break the store */
  }
};

// Map a product/cart line onto the GA4 item schema (also fine for PostHog).
const toItem = (p, index) => ({
  item_id: p.sku,
  item_name: p.seo_title || p.name || p.title,
  item_category: p.category,
  price: Number(p.retail_price ?? p.price ?? 0),
  ...(index !== undefined ? { index } : {}),
});

export const track = (event, params = {}) => {
  try {
    window.posthog?.capture(event, params);
  } catch {
    /* noop */
  }
  try {
    window.gtag?.("event", event, params);
  } catch {
    /* noop */
  }
};

export const trackViewItem = (p) =>
  track("view_item", { currency: "USD", value: p.retail_price, items: [toItem(p)] });

export const trackViewItemList = (items, listName) =>
  track("view_item_list", {
    item_list_name: listName,
    items: items.slice(0, 10).map((p, i) => toItem(p, i)),
  });

export const trackSelectItem = (p, listName) =>
  track("select_item", { item_list_name: listName, items: [toItem(p)] });

export const trackSearch = (query, results) =>
  track("search", { search_term: query, results });

export const trackAddToCart = (p, quantity) =>
  track("add_to_cart", {
    currency: "USD",
    value: (p.retail_price ?? p.price ?? 0) * (quantity || 1),
    quantity: quantity || 1,
    items: [toItem(p)],
  });

export const trackRemoveFromCart = (item) =>
  track("remove_from_cart", {
    currency: "USD",
    value: (item.price || 0) * (item.quantity || 1),
    items: [{ item_id: item.sku, item_name: item.name, price: item.price, quantity: item.quantity }],
  });

export const trackViewCart = (cart) =>
  track("view_cart", {
    currency: "USD",
    value: cart.subtotal,
    items: cart.items.map((i, n) => ({ item_id: i.sku, item_name: i.name, price: i.price, quantity: i.quantity, index: n })),
  });

export const trackBeginCheckout = (cart) =>
  track("begin_checkout", {
    currency: "USD",
    value: cart.subtotal,
    items: cart.items.map((i, n) => ({ item_id: i.sku, item_name: i.name, price: i.price, quantity: i.quantity, index: n })),
  });

export const trackAddShippingInfo = (cart, tier) =>
  track("add_shipping_info", { currency: "USD", value: cart.subtotal, shipping_tier: tier });

// purchase: call ONLY after the backend verifies payment_status === "paid".
// Guarded per session so refreshes/StrictMode never double-fire it.
export const trackPurchase = (sessionId, order) => {
  const guard = `gt_purchased_${sessionId}`;
  try {
    if (sessionStorage.getItem(guard)) return;
    sessionStorage.setItem(guard, "1");
  } catch {
    /* storage unavailable — still fire once per page load */
  }
  track("purchase", {
    transaction_id: sessionId,
    currency: "USD",
    value: order?.amount_total != null ? order.amount_total / 100 : undefined,
    items: (order?.items || []).map((i, n) => ({
      item_id: i.sku,
      item_name: i.name,
      price: i.price,
      quantity: i.quantity,
      index: n,
    })),
  });
};
